import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, Subscription } from 'rxjs';
import { PolicyService } from '../../services/policy.service';
import { Logger } from '../../../../infrastructure/logging/logger';
import { Country } from '../../../../shared/address/Country';
import { CountryService } from '../../../../shared/reference/country.service';
import { PriceRatesService } from '../../../price-rates/services/price-rates.service';
import { Underwriter } from '../../../underwriters/models/Underwriter';
import { UnderwritersService } from '../../../underwriters/services/underwriters.service';
import { PolicyUnderwriter, PolicyUnderwriterRow, PolicyPrice, Policy } from '../../models/Policy';
import { AddRemovePolicyCountriesComponent } from '../tabs/policy-countries/components/add-remove-policy-countries/add-remove-policy-countries.component';
import { DeletePolicyUnderwriterDialogComponent } from '../tabs/policy-underwriters/components/delete-policy-underwriter-dialog/delete-policy-underwriter-dialog.component';
import { SelectPolicyUnderwriterDialogComponent } from '../tabs/policy-underwriters/components/select-policy-underwriter-dialog/select-policy-underwriter-dialog.component';
import { DeactivatePolicyDialogComponent } from './components/deactivate-policy-dialog/deactivate-policy-dialog.component';
import { PolicyInfoComponent } from '../tabs/policy-info/policy-info.component';
import { PolicyUnderwritersComponent } from '../tabs/policy-underwriters/policy-underwriters.component';
import { BackButtonComponent } from '../../../../shared/components/back-button/back-button.component';
import { NavService } from '../../../../layout/nav/nav.service';
import { OrganizationService } from '../../../organizations/services/organizations.service';

@Component({
  selector: 'app-policy-detail',
  templateUrl: './policy-detail.component.html',
  styleUrls: ['./policy-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatCard,
    MatTabsModule,
    MatProgressSpinner,
    MatButtonModule,
    PolicyInfoComponent,
    PolicyUnderwritersComponent,
    BackButtonComponent
  ]
})
export class PolicyDetailComponent implements OnInit {

  private routeSub?: Subscription;
  @ViewChild(MatTabGroup) matTabGroup!: MatTabGroup;

  allUnderwriters!: Underwriter[];
  availableUnderwriters!: Underwriter[];
  selectedUnderwriter?: Underwriter;
  policyUnderwriters!: PolicyUnderwriter[];
  policyUnderwriterDatasource = new MatTableDataSource<PolicyUnderwriterRow>();
  priceRates!: PolicyPrice[];
  countries!: Country[];

  loading = new BehaviorSubject<boolean>(false);
  policy: any;
  policyId!: string;
  currentOrgId!: any;
  tabIndex: number = 0;
  selectedPolicyActive: boolean = false;
  underwritersAvailableBoolean: boolean = true;
  policyInUseBoolean: boolean = false;

  constructor(
    private router: Router,
    private policyService: PolicyService,
    private priceRateService: PriceRatesService,
    private underwritersService: UnderwritersService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private logService: Logger,
    private countryService: CountryService,
    private navService: NavService,
    private organizationService: OrganizationService
  ) { }

  ngOnInit() {
    this.routeSub = this.route.params.subscribe(params => {
      this.policyId = params['id'];
      this.loadPage();
    });

    this.tabIndex = parseInt(this.route.snapshot.queryParamMap.get('tab') || '', 10);
    if (isNaN(this.tabIndex) || this.tabIndex < 0) {
      this.tabIndex = 0;
    }
  }

  async loadPage() {
    try {
      this.loading.next(true);

      const [policy] = await Promise.all([
        this.policyService.getPolicyDetail(this.policyId),
        this.countryService.getCountries().then(countries => this.countries = countries),
        this.getPriceRates(),
        this.getUnderwriters()
      ]);
      this.policy = policy;
      this.setPolicyActiveStatus(this.policy);

      await this.getUnderwritersByPolicyCoverageId(this.policy.id);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Policy details', 'Error while retrieving Policy details');
    } finally {
      this.loading.next(false);
    }
  }

  async checkPolicyOrgAndPropertyAssignments(policy: Policy) {
    if (this.navService.selectedOrgId.value) {
      this.currentOrgId = this.navService.selectedOrgId.value;
    } else {
      this.currentOrgId = 0;
    }

    // TODO: Replace with new Org & Property check endpoint when completed
    if (this.currentOrgId > 0) {
      const organizationPolicyResponse = await this.organizationService.getPoliciesByOrganizationId(this.currentOrgId);
      if (organizationPolicyResponse.some(orgPolicy => orgPolicy.insurance_policy_id === policy.id)) {
        this.policyInUseBoolean = true;
      }
    }

    const propertyPoliciesResponse = await this.policyService.getPropertyPolicies();
    if (propertyPoliciesResponse.some(propPolicy => propPolicy.insurance_policy_id === policy.id)) {
      this.policyInUseBoolean = true;
    }
  }

  async getUnderwriters() {
    this.allUnderwriters = [];
    const underwriterResponse = await this.underwritersService.getUnderwriters();
    this.allUnderwriters = underwriterResponse;
  }

  async getUnderwritersByPolicyCoverageId(id: number) {
    this.policyUnderwriterDatasource.data = [];
    this.availableUnderwriters = [];

    this.policyUnderwriters = await this.underwritersService.getPolicyUnderwritersByPolicyCoverageId(id);
    if (this.policyUnderwriters.length === 0) {
      this.checkPolicyOrgAndPropertyAssignments(this.policy);
      this.availableUnderwriters = this.allUnderwriters;
    } else {
      this.policyInUseBoolean = true;

      this.policyUnderwriterDatasource = new MatTableDataSource(this.policyUnderwriters.map((underwriter: any) => this.policyService.getPolicyUnderwriterRowFromUndewriter(underwriter)));

      this.availableUnderwriters = this.allUnderwriters.filter(o => !this.policyUnderwriters.find((o2: any) => o.id === o2.underwriter_id));
      if (this.availableUnderwriters.length === 0) {
        this.underwritersAvailableBoolean = false;
      }
    }
  }

  async getPriceRates() {
    this.priceRates = [];
    const response = await this.priceRateService.getPriceRates();
    this.priceRates = response;
  }

  setPolicyActiveStatus(policy: Policy | null) {
    if (!policy) {
      this.selectedPolicyActive = false;
      return;
    }
    this.selectedPolicyActive = policy.status_id === 1;
  }

  async onSetPolicyStatusClick(): Promise<void> {
    const dialogRef = this.dialog.open(DeactivatePolicyDialogComponent, {
      width: '60vw',
      maxHeight: '275px',
      disableClose: true,
      data: { 
        policy: this.policy,
        isPolicyActive: this.selectedPolicyActive ? true : false,
        isPolicyInUse: this.policyInUseBoolean
      }
    });
    dialogRef.afterClosed().subscribe(async confirmed => {
      if (!confirmed) {
        return;
      }

      let statusId;
      if (this.selectedPolicyActive) {
        statusId = 2;
      } else {
        statusId = 1;
      }
      await this.policyService.updatePolicyStatus(Number(this.policyId), { status_id: statusId });
      this.navigateToPoliciesPage();
    });
  }

  onEditPolicyInfoClick() {
    this.router.navigate(['/policies/form'], { queryParams: { id: this.policyId } });
  }

  async onAssignUnderwriterClick() {
    this.openSelectUnderwriterDialog();
  }

  async openSelectUnderwriterDialog(selectedUnderwriter?: any) {
    if (selectedUnderwriter) {
      let existingPolicyUnderwriters = this.allUnderwriters.filter(o => this.policyUnderwriters.find((o2: any) => o.id === o2.underwriter_id));
      let selectUnderwriter = existingPolicyUnderwriters.find(underwriter => underwriter.id === selectedUnderwriter.underwriter_id);
      if (selectUnderwriter) {
        this.availableUnderwriters.push(selectUnderwriter);
      }
    }

    const underwriterDialogRef = this.dialog.open(SelectPolicyUnderwriterDialogComponent, {
      minWidth: '55%',
      maxHeight: '800px',
      disableClose: false,
      autoFocus: false,
      data: {
        policyId: this.policyId,
        underwriters: this.availableUnderwriters,
        priceRates: this.priceRates,
        selectedUnderwriter: selectedUnderwriter ? selectedUnderwriter : []
      }
    });
    underwriterDialogRef.afterClosed().subscribe(res => {
      if (res) {
        if (selectedUnderwriter) {
          this.underwritersService.updatePolicyUnderwriter(selectedUnderwriter.id, res).then(_ => {
            this.loadPage();
          });
        } else {
          this.underwritersService.createPolicyUnderwriter(res).then(_ => {
            this.loadPage();
          });
        }
      } else {
        this.selectedUnderwriter = undefined;
        this.availableUnderwriters = this.allUnderwriters.filter(o => !this.policyUnderwriters.find((o2: any) => o.id === o2.underwriter_id));
      }
    });
  }

  async openDeleteUnderwriterDialog(selectedUnderwriter: any) {
    const deleteUnderwriterDialogRef = this.dialog.open(DeletePolicyUnderwriterDialogComponent, {
      width: '50vw',
      maxHeight: '265px',
      disableClose: false,
      data: 'Underwriter'
    });
    deleteUnderwriterDialogRef.afterClosed().subscribe(res => {
      if (res > 0) {
        let payload = {
          is_active: 0,
          original_id: selectedUnderwriter.id
        }
        this.underwritersService.updatePolicyUnderwriter(selectedUnderwriter.id, payload).then(_ => {
          this.loadPage();
          this.underwritersAvailableBoolean = true;
        });
      }
    });
  }

  async onAddRemoveCountryClick() {
    let existingCountries: any[] = [];
    let ctr = 0;
    this.policy.countries.forEach((country: any) => {
      this.countryService.getCountryFromId(country.country_id).then(c => {
        ctr++;
        existingCountries.push(c);
        if (ctr === this.policy.countries.length) {
          this.openAddRemoveCountryDialog(existingCountries);
        }
      });
    });
  }

  openAddRemoveCountryDialog(existingCountries: any) {
    let startingCountryArr: any[] = [];
    existingCountries.forEach((country: any) => {
      startingCountryArr.push(country);
    });

    const countryDialogRef = this.dialog.open(AddRemovePolicyCountriesComponent, {
      width: '80vw',
      height: '415px',
      disableClose: false,
      data: {
        existingCountries: existingCountries,
        countries: this.countries
      }
    });
    countryDialogRef.afterClosed().subscribe(async updatedCountryArr => {
      if (!updatedCountryArr) {
        return;
      }

      if (updatedCountryArr.length > 0) {
        try {
          let createCountryArr = updatedCountryArr.filter((o: any) => !startingCountryArr.find(o2 => o.id === o2.id));
          if (createCountryArr.length > 0) {
            let ctr = 0;
            createCountryArr.forEach((country: any) => {
              ctr++;
              let payload = {
                policy_id: Number(this.policyId),
                country_id: country.id
              }
              this.policyService.createPolicyCountry(payload);
  
              if (ctr === createCountryArr.length) {
                this.loadPage();
              }
            });
          }
  
          let deleteCountryArr = startingCountryArr.filter((o: any) => !updatedCountryArr.find((o2: any) => o.id === o2.id));
          if (deleteCountryArr.length > 0) {
            let ctr = 0;
            deleteCountryArr.forEach((country: any) => {
              ctr++;
              this.policyService.deletePolicyCountry(Number(this.policyId), country.id);
  
              if (ctr === deleteCountryArr.length) {
                this.loadPage();
              }
            });
          }
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Policy country', 'Error while updating Policy country');
        }
      }
    });
  }

  buildPolicyCountryPayload(countries: any) {
    countries.forEach((country: any) => {
      let payload = {
        policy_id: Number(this.policyId),
        country_id: country.id
      }
      return payload;
    });
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  navigateToPoliciesPage() {
    this.router.navigate(['/policies']);
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent) {
    this.tabIndex = tabChangeEvent.index;
    this.router.navigate([], { relativeTo: this.route });
  }
}